import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx, Teleport as _Teleport, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = ["onClick"]
const _hoisted_3 = ["src", "autoplay", "muted"]

import { ref, onMounted, onUnmounted } from 'vue';
import { ElDialog } from 'element-plus';
import { debounce } from '@/utils/debounce';

import Play from '@/components/VideoPlayer/Buttons/Play.vue';
import Volume from '@/components/VideoPlayer/Buttons/Volume.vue';
import PlaybackRate from '@/components/VideoPlayer/Buttons/PlaybackRate.vue';
import Fullscreen from '@/components/VideoPlayer/Buttons/Fullscreen.vue';
import VideoProgress from '@/components/VideoPlayer/VideoProgress.vue';
import Cross from '@/components/icons/videoplayer/Cross.vue';

import IconPlay from '@/components/icons/videoplayer/Play.vue';
import IconPause from '@/components/icons/videoplayer/Pause.vue';

const cssClass = 'videoplayer';


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    path: {},
    width: {},
    title: {},
    autoplay: { type: Boolean },
    openButtonCssClass: {},
    openButtonText: {}
  },
  setup(__props: any) {



const dialogVisible = ref<boolean>(false);

const $header = ref<HTMLElement | null>(null);
const $container = ref<HTMLElement | null>(null);
const $video = ref<HTMLVideoElement | null>(null);
const $controls = ref<HTMLElement | null>(null);
const $playbackAnimation = ref<HTMLElement | null>(null);

const duration = ref<number>(0);
const elapsed = ref<number>(0);
const currentVolume = ref<number>(1);

const isPaused = ref<boolean>(true);
const isMuted = ref<boolean>(false);
const isFullscreen = ref<boolean>(false);
const isHeaderVisible = ref<boolean>(true);
const areControlsVisible = ref<boolean>(true);

const initializeVideo = (): void => {
  if (!$video.value) { return; }

  duration.value = Math.round($video.value.duration);
};

const animatePlayback = (): void => {
  $playbackAnimation?.value?.animate([
    {
      opacity: 1,
      transform: 'scale(1)',
    },
    {
      opacity: 0,
      transform: 'scale(1.3)',
    },
  ], {
    duration: 500,
  });
};

const hideControls = debounce((): void => {
  if (!$video.value || $video.value.paused) { return; }

  if ($controls.value && $header.value) {
    areControlsVisible.value = false;
    isHeaderVisible.value = false;
  }
}, 3000);

const showControls = (): void => {
  if (
    $controls.value
    && $header.value
    && !areControlsVisible.value
  ) {
    areControlsVisible.value = true;

    if (!isFullscreen.value) {
      isHeaderVisible.value = true;
    }
  }

  hideControls();
};

const handlePlay = (): void => {
  if (!$video.value) { return; }

  if ($video.value.paused || $video.value.ended) {
    $video.value.play();
    hideControls();
  } else {
    $video.value.pause();
    showControls();
  }

  animatePlayback();
};

const updateProgress = (): void => {
  if (!$video.value) { return; }
  elapsed.value = Math.round($video.value.currentTime);
};

const handleMute = (value: boolean): void => {
  if (!$video.value) { return; }
  isMuted.value = value;
};

const toggleMuteWithKey = (): void => {
  if (!$video.value) { return; }
  isMuted.value = !isMuted.value;
};

const handleVolumeChange = (volume: number): void => {
  if (!$video.value) { return; }
  $video.value.volume = volume;
  isMuted.value = $video.value.volume === 0;
};

const updateCurrentVolume = () => {
  if (!$video.value) { return; }
  currentVolume.value = $video.value.volume;
};

const handlePlaybackRate = (rate: number): void => {
  if (!$video.value) { return; }
  $video.value.playbackRate = rate;
};

const handleFullscreen = (): void => {
  if ($container.value) {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if ((document as any).webkitFullscreenElement) {
      (document as any).webkitExitFullscreen();
    } else if (($container.value as any).webkitRequestFullscreen) {
      ($container.value as any).webkitRequestFullscreen();
    } else {
      $container.value.requestFullscreen();
    }
  }
};

const handleSkip = (skipTo: number): void => {
  if (!$video.value) {
    return;
  }

  $video.value.currentTime = skipTo;
};

const keyboardShortcuts = (event: { code: string; }): void => {
  const { code } = event;

  // eslint-disable-next-line default-case
  switch (code) {
    case 'Space':
      handlePlay();
      break;
    case 'KeyM':
      toggleMuteWithKey();
      break;
    case 'KeyF':
    case 'Escape':
      handleFullscreen();
      break;
  }
};

const handleModalClose = (done: () => void) => {
  if (!$video.value) { return; }
  $video.value.pause();
  done();
};

onMounted(() => {
  const fullscreenChange: string[] = [
    'fullscreenchange',
    'webkitfullscreenchange',
    'mozfullscreenchange',
    'msfullscreenchange',
  ];

  fullscreenChange.forEach((event) => {
    document.addEventListener(event, () => {
      isFullscreen.value = Boolean(document.fullscreenElement);
    });
  });

  document.addEventListener('keyup', keyboardShortcuts);
});

onUnmounted(() => {
  document.removeEventListener('keyup', keyboardShortcuts);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      class: _normalizeClass(`${_ctx.openButtonCssClass}`),
      onClick: _cache[0] || (_cache[0] = ($event: any) => (dialogVisible.value = true))
    }, [
      _renderSlot(_ctx.$slots, "video-preview"),
      _renderSlot(_ctx.$slots, "btn-text", {}, () => [
        _createTextVNode(_toDisplayString(_ctx.openButtonText), 1)
      ])
    ], 2),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (dialogVisible.value)
        ? (_openBlock(), _createBlock(_unref(ElDialog), {
            key: 0,
            modelValue: dialogVisible.value,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((dialogVisible).value = $event)),
            width: _ctx.width,
            class: _normalizeClass(`${cssClass}-modal`),
            beforeClose: handleModalClose,
            showClose: false,
            closeOnPressEscape: false,
            alignCenter: true
          }, {
            header: _withCtx(({ close, titleId, titleClass }) => [
              _withDirectives(_createElementVNode("div", {
                ref_key: "$header",
                ref: $header,
                class: "videoplayer-modal__header"
              }, [
                _createElementVNode("h3", {
                  id: titleId,
                  class: _normalizeClass(titleClass)
                }, _toDisplayString(_ctx.title), 11, _hoisted_1),
                _createElementVNode("button", {
                  class: "videoplayer-modal__close-btn videoplayer__btn",
                  "aria-label": "Закрыть",
                  onClick: close
                }, [
                  _createVNode(Cross)
                ], 8, _hoisted_2)
              ], 512), [
                [_vShow, isHeaderVisible.value]
              ])
            ]),
            default: _withCtx(() => [
              _createElementVNode("div", {
                ref_key: "$container",
                ref: $container,
                class: _normalizeClass(`${cssClass}__container`)
              }, [
                _createElementVNode("div", {
                  ref_key: "$playbackAnimation",
                  ref: $playbackAnimation,
                  class: _normalizeClass(`${cssClass}__playback-animation`)
                }, [
                  (!isPaused.value)
                    ? (_openBlock(), _createBlock(IconPlay, { key: 0 }))
                    : (_openBlock(), _createBlock(IconPause, { key: 1 }))
                ], 2),
                _createElementVNode("video", {
                  ref_key: "$video",
                  ref: $video,
                  src: _ctx.path,
                  width: "960px",
                  height: "auto",
                  class: _normalizeClass(`${cssClass}`),
                  preload: "metadata",
                  autoplay: _ctx.autoplay,
                  muted: isMuted.value,
                  onLoadedmetadata: initializeVideo,
                  onTimeupdate: updateProgress,
                  onMousemove: showControls,
                  onVolumechange: updateCurrentVolume,
                  onPlay: _cache[1] || (_cache[1] = ($event: any) => (isPaused.value = false)),
                  onPause: _cache[2] || (_cache[2] = ($event: any) => (isPaused.value = true)),
                  onEnded: _cache[3] || (_cache[3] = ($event: any) => (isPaused.value = true)),
                  onStalled: _cache[4] || (_cache[4] = ($event: any) => (isPaused.value = true)),
                  onClick: handlePlay
                }, null, 42, _hoisted_3),
                _withDirectives(_createElementVNode("div", {
                  ref_key: "$controls",
                  ref: $controls,
                  class: _normalizeClass(`${cssClass}__controls`),
                  onMousemove: showControls
                }, [
                  _createVNode(Play, {
                    isPaused: isPaused.value,
                    onClick: handlePlay
                  }, null, 8, ["isPaused"]),
                  _createVNode(VideoProgress, {
                    progress: elapsed.value,
                    duration: duration.value,
                    onSkipTo: handleSkip
                  }, null, 8, ["progress", "duration"]),
                  _createVNode(Volume, {
                    currentVolume: currentVolume.value,
                    isMuted: isMuted.value,
                    onClick: handleMute,
                    onVolumeInput: handleVolumeChange
                  }, null, 8, ["currentVolume", "isMuted"]),
                  _createVNode(PlaybackRate, { onClick: handlePlaybackRate }),
                  _createVNode(Fullscreen, {
                    isFullscreen: isFullscreen.value,
                    onClick: handleFullscreen
                  }, null, 8, ["isFullscreen"])
                ], 34), [
                  [_vShow, areControlsVisible.value]
                ])
              ], 2)
            ]),
            _: 1
          }, 8, ["modelValue", "width", "class"]))
        : _createCommentVNode("", true)
    ]))
  ], 64))
}
}

})